import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Per-user pinned-agent list, persisted **on the server** so it follows
 * the user across devices and browsers. localStorage is kept as an
 * instant-render cache + offline fallback (also scoped per email so a
 * shared demo machine never leaks pins between users).
 *
 * Storage layers, in priority order on a fresh render:
 *   1. localStorage["jobotics.pinnedAgents:<email>"]  — synchronous,
 *      lets the sidebar paint immediately on page load.
 *   2. GET /api/auth/pinned-agents                    — authoritative,
 *      replaces local state when it lands.
 *
 * Writes:
 *   1. Optimistic local state update  → sidebar re-renders instantly.
 *   2. Cache to localStorage.
 *   3. Debounced PUT /api/auth/pinned-agents          — 600ms after the
 *      last toggle, so rapid clicks coalesce into one request.
 *
 * If the API call fails (offline, 401, 503 missing-column), local state
 * sticks and the user can keep working; the next successful PUT will
 * reconcile.
 *
 * Custom-event broadcast (`jobotics:pinnedAgentsChanged`) keeps sibling
 * hook instances in the same tab (sidebar + agent-page header) in sync
 * without prop drilling. Native `storage` event handles cross-tab sync.
 *
 * Usage:
 *   const { pinned, isPinned, togglePin } = usePinnedAgents();
 *   isPinned('labor_estimator')   // → boolean
 *   togglePin('labor_estimator')  // → flips state + persists
 */
const KEY_PREFIX = 'jobotics.pinnedAgents:';
const MAX_PINNED = 20;
const SAME_TAB_EVENT = 'jobotics:pinnedAgentsChanged';
const PUT_DEBOUNCE_MS = 600;

const storageKeyFor = (email) => `${KEY_PREFIX}${(email || '').toLowerCase()}`;

const readStorage = (email) => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(storageKeyFor(email));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => typeof x === 'string' && x.length > 0 && x.length < 100)
      .slice(0, MAX_PINNED);
  } catch (_) {
    return [];
  }
};

const writeStorage = (email, ids) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(storageKeyFor(email), JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: { email } }));
  } catch (_) {
    /* quota / private browsing — silent */
  }
};

export default function usePinnedAgents() {
  const { user } = useAuth();
  const email = user?.email || '';
  const [pinned, setPinned] = useState(() => readStorage(email));
  const putTimerRef = useRef(null);
  const putAbortRef = useRef(null);

  // 1. Whenever the user changes, paint from localStorage immediately so
  //    the sidebar doesn't flash empty during the GET fetch.
  useEffect(() => {
    setPinned(readStorage(email));
  }, [email]);

  // 2. Hydrate from the server. Authoritative source — replaces local
  //    state and refreshes the localStorage cache. We don't block on this
  //    so the UI stays responsive; failures degrade to localStorage-only.
  useEffect(() => {
    if (!email) return undefined;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch('/api/auth/pinned-agents', {
          method: 'GET',
          credentials: 'include',
          signal: ctrl.signal,
        });
        if (!r.ok) return; // 401 / 500 — stay on cached state silently
        const data = await r.json();
        if (Array.isArray(data?.pinned)) {
          setPinned(data.pinned);
          writeStorage(email, data.pinned);
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.warn('[usePinnedAgents] hydrate failed:', err?.message);
        }
      }
    })();
    return () => ctrl.abort();
  }, [email]);

  // 3. Cross-tab + same-tab sync.
  useEffect(() => {
    const key = storageKeyFor(email);
    const onStorage = (e) => {
      if (e.key === key) setPinned(readStorage(email));
    };
    const onSameTab = (e) => {
      if (!e?.detail || !e.detail.email || e.detail.email === email) {
        setPinned(readStorage(email));
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SAME_TAB_EVENT, onSameTab);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onSameTab);
    };
  }, [email]);

  // Debounced server write. Called by togglePin/unpinAll after optimistic
  // local update. Cancels any in-flight PUT so rapid clicks send just one
  // final request.
  const schedulePersist = useCallback(
    (ids) => {
      if (!email) return; // anon — local only
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
      putTimerRef.current = setTimeout(async () => {
        putTimerRef.current = null;
        if (putAbortRef.current) putAbortRef.current.abort();
        putAbortRef.current = new AbortController();
        try {
          await fetch('/api/auth/pinned-agents', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pinned: ids }),
            signal: putAbortRef.current.signal,
          });
        } catch (err) {
          if (err?.name !== 'AbortError') {
            console.warn('[usePinnedAgents] persist failed:', err?.message);
          }
        }
      }, PUT_DEBOUNCE_MS);
    },
    [email]
  );

  const isPinned = useCallback((id) => pinned.includes(id), [pinned]);

  const togglePin = useCallback(
    (id) => {
      if (!id || typeof id !== 'string') return;
      setPinned((prev) => {
        const next = prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id].slice(-MAX_PINNED);
        writeStorage(email, next);
        schedulePersist(next);
        return next;
      });
    },
    [email, schedulePersist]
  );

  const unpinAll = useCallback(() => {
    setPinned([]);
    writeStorage(email, []);
    schedulePersist([]);
  }, [email, schedulePersist]);

  // Cleanup any pending timer on unmount so React strict-mode double-
  // mount doesn't leave a stale write firing.
  useEffect(() => {
    return () => {
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
      if (putAbortRef.current) putAbortRef.current.abort();
    };
  }, []);

  return { pinned, isPinned, togglePin, unpinAll };
}
